import {
  Alert,
  Col,
  Form,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import useModal from 'stores/useModal';
import { useGetKinSimCampaigns } from 'hooks/useGetServices';
import useBookingStore from 'stores/useBooking';
import { serviceIssuseKinSim } from 'services/api/module/service.api';
import QUERY_KEYS from 'services/api/queryKeys';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const QUANTITY_LIMITS = {
  MIN: 1,
  MAX: 10
} as const;

interface IssueKinSimFormValues {
  guest: number;
  esim_campaign: number;
  quantity: number;
}

function IssueKinSimModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    isOpenIssueKinSim,
    setIsOpenIssueKinSim,
    service_id,
    campaign_id,
    current_service_qty,
    current_guest_id
  } = useModal();
  const { bookingLineDetail } = useBookingStore();
  const { data: esimCampaigns, isLoading } = useGetKinSimCampaigns(isOpenIssueKinSim);

  const { mutateAsync: mutateIssueKinSim } = useMutation({
    mutationFn: (values: IssueKinSimFormValues) =>
      serviceIssuseKinSim({
        service_id,
        guest_id: values.guest,
        campaign_id: values.esim_campaign,
        quantity: values.quantity
      })
  });

  const availableCampaigns = esimCampaigns?.filter(campaign => campaign.available_urls > 0) ?? [];
  const guests = bookingLineDetail?.booking_line_guests ?? [];
  const hasGuests = guests.length > 0;
  const hasCampaigns = availableCampaigns.length > 0;

  // Initialize form values when modal opens or when props change
  useEffect(() => {
    if (isOpenIssueKinSim && hasCampaigns && hasGuests) {
      form.setFieldsValue({
        quantity: current_service_qty || QUANTITY_LIMITS.MIN,
        esim_campaign: campaign_id || undefined,
        guest: undefined
      });
    }
  }, [isOpenIssueKinSim, current_service_qty, campaign_id, hasCampaigns, hasGuests, form]);

  const resetModalState = useCallback(() => {
    form.resetFields();
    setIsOpenIssueKinSim(false, 0, 0, 0);
  }, [setIsOpenIssueKinSim, form]);

  const handleIssueKinSim = useCallback(
    async (values: IssueKinSimFormValues) => {
      setIsSubmitting(true);
      try {
        await mutateIssueKinSim(values);

        // Refresh eSIM UI that may be open behind this modal
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_ESIM_URL, service_id] });

        if (bookingLineDetail?.booking_line_id) {
          queryClient.invalidateQueries({
            queryKey: [
              QUERY_KEYS.KEY_ROOM,
              QUERY_KEYS.GET_BOOKING_DETAIL,
              bookingLineDetail.booking_line_id
            ]
          });
        }

        notification.success({
          message: t('common.message.esimIssuedToGuest', { quantity: values.quantity })
        });

        resetModalState();
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.message.issueKinSimFailed')
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      mutateIssueKinSim,
      queryClient,
      service_id,
      bookingLineDetail?.booking_line_id,
      t,
      resetModalState
    ]
  );

  const handleCancel = useCallback(() => {
    if (!isSubmitting) {
      resetModalState();
    }
  }, [isSubmitting, resetModalState]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
          <Spin size="large" tip={t('common.loading')} />
        </div>
      );
    }

    if (!hasCampaigns || !hasGuests) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {!hasCampaigns && (
            <Alert
              message={t('esim.noCampaignsAvailable')}
              description={t('esim.noCampaignsDescription')}
              type="warning"
              showIcon
            />
          )}
          {!hasGuests && (
            <Alert
              message={t('esim.noGuestsAvailable')}
              description={t('esim.noGuestsDescription')}
              type="warning"
              showIcon
            />
          )}
        </Space>
      );
    }

    return (
      <Form form={form} onFinish={handleIssueKinSim} layout="vertical">
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              label={t('esim.campaign')}
              name="esim_campaign"
              rules={[
                {
                  required: true,
                  message: t('esim.campaignRequired')
                }
              ]}
              tooltip={t('esim.campaignTooltip')}
            >
              <Select
                placeholder={t('esim.selectCampaign')}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                disabled={!!campaign_id}
                options={availableCampaigns.map(campaign => ({
                  label: `${campaign.name} (${campaign.available_urls} ${t('esim.available')})`,
                  value: campaign.id
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={t('common.guest')}
              name="guest"
              rules={[
                {
                  required: true,
                  message: t('esim.guestRequired')
                }
              ]}
            >
              <Select
                placeholder={t('esim.selectGuest')}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={guests.map(guest => ({
                  label: guest.email ? `${guest.guest_name} (${guest.email})` : guest.guest_name,
                  value: guest.id,
                  disabled: current_guest_id === guest.id
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={t('common.quantity')}
              name="quantity"
              rules={[
                {
                  required: true,
                  message: t('esim.quantityRequired')
                },
                {
                  type: 'number',
                  min: QUANTITY_LIMITS.MIN,
                  max: QUANTITY_LIMITS.MAX,
                  message: t('esim.quantityRange', {
                    min: QUANTITY_LIMITS.MIN,
                    max: QUANTITY_LIMITS.MAX
                  })
                }
              ]}
            >
              <InputNumber
                min={QUANTITY_LIMITS.MIN}
                max={QUANTITY_LIMITS.MAX}
                style={{ width: '100%' }}
                placeholder={t('esim.enterQuantity', {
                  min: QUANTITY_LIMITS.MIN,
                  max: QUANTITY_LIMITS.MAX
                })}
                disabled={current_service_qty === 1}
              />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('esim.quantityNote')}
            </Text>
          </Col>
        </Row>
      </Form>
    );
  };

  const canSubmit = hasCampaigns && hasGuests && !isSubmitting;

  return (
    <Modal
      title={t('esim.issueTitle')}
      className="modal-confirm"
      centered
      open={isOpenIssueKinSim}
      okText={t('esim.issueButton')}
      cancelText={t('common.actions.cancelAction')}
      okButtonProps={{
        className: 'ant-btn-secondary',
        loading: isSubmitting,
        disabled: !canSubmit
      }}
      cancelButtonProps={{ disabled: isSubmitting }}
      onOk={() => canSubmit && form.submit()}
      onCancel={handleCancel}
      destroyOnClose
      width={520}
      zIndex={1100}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
    >
      {renderContent()}
    </Modal>
  );
}

export default IssueKinSimModal;
